/**
 * RiskBreakdown — shows the factor-by-factor reasoning behind a
 * customer's risk score. Used in CustomerProfile (full panel) and
 * LoanDialog (compact mode).
 *
 * Props:
 *   - customerId   → fetches /customers/{id}/risk live
 *   - compact      → true: header + top 4 factors, false: full panel
 *   - showHeader   → render the colored hero strip (default true)
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  ShieldAlert, ShieldCheck, Shield, AlertTriangle, CheckCircle2, XCircle,
} from "lucide-react";

const TONE = {
  high:   { bg: "bg-red-50",     border: "border-red-300",     text: "text-red-700",     Icon: ShieldAlert },
  medium: { bg: "bg-amber-50",   border: "border-amber-300",   text: "text-amber-800",   Icon: Shield },
  low:    { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", Icon: ShieldCheck },
};

const STATUS_ICON = {
  bad:  XCircle,
  warn: AlertTriangle,
  ok:   CheckCircle2,
};
const STATUS_COLOR = {
  bad:  "text-red-600 bg-red-50",
  warn: "text-amber-700 bg-amber-50",
  ok:   "text-emerald-700 bg-emerald-50",
};

export default function RiskBreakdown({ customerId, compact = false, showHeader = true }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    api.get(`/customers/${customerId}/risk`)
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => { if (active) setErr(e?.response?.data?.detail || "Risk fetch failed"); });
    return () => { active = false; };
  }, [customerId]);

  if (err) {
    return <div className="text-sm text-red-600" data-testid="risk-breakdown-error">{String(err)}</div>;
  }
  if (!data) {
    return <div className="text-sm text-slate-400 animate-pulse" data-testid="risk-breakdown-loading">Risk calculate ho raha hai…</div>;
  }

  const lvl = data.risk_level || "low";
  const t = TONE[lvl] || TONE.low;
  const factors = data.risk_breakdown || [];
  // Show all by default; in compact mode show top 4 by impact.
  const sorted = [...factors].sort((a, b) => (b.impact || 0) - (a.impact || 0));
  const visible = compact ? sorted.slice(0, 4) : sorted;
  const hidden = compact ? Math.max(0, sorted.length - visible.length) : 0;

  return (
    <div className="space-y-3" data-testid="risk-breakdown">
      {showHeader && (
        <div className={`flex items-center gap-3 rounded-md border px-4 py-3 ${t.bg} ${t.border} ${t.text}`}>
          <t.Icon className="w-6 h-6 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold capitalize text-lg leading-tight" data-testid="risk-header-level">
              {lvl} Risk
            </div>
            <div className="text-xs opacity-80" data-testid="risk-header-score">
              Score: <span className="font-bold">{data.risk_score}/100</span> — lower is better
            </div>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        {visible.map((f) => {
          const Icon = STATUS_ICON[f.status] || AlertTriangle;
          const tone = STATUS_COLOR[f.status] || "text-slate-700 bg-slate-50";
          return (
            <div
              key={f.factor}
              className="flex items-start gap-2.5 rounded-md border border-slate-200 bg-white px-3 py-2"
              data-testid={`risk-factor-${f.factor}`}
              title={f.detail}
            >
              <div className={`shrink-0 rounded-full p-1 ${tone}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm leading-tight truncate">{f.label}</div>
                  {f.impact > 0 ? (
                    <span className="shrink-0 text-[11px] font-bold tabular text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                      +{f.impact}
                    </span>
                  ) : (
                    <span className="shrink-0 text-[11px] font-bold tabular text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                      OK
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{f.detail}</div>
              </div>
            </div>
          );
        })}
        {hidden > 0 && (
          <div className="text-xs text-slate-500" data-testid="risk-hidden-count">
            + {hidden} aur factors — full breakdown profile par dekhein
          </div>
        )}
      </div>
    </div>
  );
}
